import React, { useState, useEffect } from 'react';
import { createUser, getAllRoles } from '../../services/userService';
import { getAllGroups } from '../../services/groupService';
import { SuccessCircle, WarningIcon } from '../../assets/icons';
import Spinner from '../common/Spinner';
import RoleBadge from '../common/RoleBadge';
import useModalClose from '../../hooks/useModalClose';
import useIlceMahalleSecimi from '../../hooks/useIlceMahalleSecimi';
import IlceMahalleSecimi from './IlceMahalleSecimi';

const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};

// Role id for Denetleyici - check by id since name can change
const DENETLEYICI_ROLE_ID = 6;

const INITIAL_FORM_STATE = {
  name: '',
  surname: '',
  tcnumber: '',
  email: '',
  phoneNumber: '05',
  role: ''
};

const CreateUserModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [createdUser, setCreatedUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(true);

  const isDenetleyici = formData.role === String(DENETLEYICI_ROLE_ID);
  // Rol Denetleyici'den çıkınca seçim hook içinde temizlenir.
  const ilceMahalle = useIlceMahalleSecimi({ aktif: isDenetleyici });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsData, rolesData] = await Promise.all([
          getAllGroups(),
          getAllRoles()
        ]);
        setGroups(groupsData);
        setRoles(rolesData);
      } catch (err) {
        console.error('Failed to fetch groups or roles:', err);
        setRoles([]);
      } finally {
        setIsRolesLoading(false);
      }
    };
    fetchData();
  }, []);

  // Set default role when roles load or when form was reset (e.g. modal reopened)
  useEffect(() => {
    if (roles.length > 0 && !formData.role) {
      setFormData((prev) => ({ ...prev, role: String(roles[0].id) }));
    }
  }, [roles, formData.role]);

  const handleGroupChange = (userId, newGroupName) => {
    setFormData((prev) => ({
      ...prev,
      grup: newGroupName,
    }));
  }


  // Reset all state when modal closes
  const resetState = () => {
    setCurrentStep(STEPS.FORM);
    setIsLoading(false);
    setError(null);
    setFormData(INITIAL_FORM_STATE);
    setCreatedUser(null);
  };

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  // Add modal closing behaviors
  useModalClose(isOpen, onClose);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phoneNumber') {
      // If the value is empty or less than 2 characters, reset to '05'
      if (value.length < 2) {
        setFormData(prev => ({
          ...prev,
          [name]: '05'
        }));
        return;
      }

      // Only allow numbers
      const numbersOnly = value.replace(/\D/g, '');
      
      // If the number doesn't start with '05', force it
      if (!numbersOnly.startsWith('05')) {
        const correctedNumber = '05' + numbersOnly.slice(2);
        setFormData(prev => ({
          ...prev,
          [name]: correctedNumber.slice(0, 11)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: numbersOnly.slice(0, 11)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name || !formData.surname || !formData.tcnumber || 
        !formData.phoneNumber || !formData.role) {
      setError('Lütfen tüm alanları doldurunuz.');
      return false;
    }
    if (isDenetleyici) {
      const ilceMahalleHatasi = ilceMahalle.dogrula();
      if (ilceMahalleHatasi) {
        setError(ilceMahalleHatasi);
        return false;
      }
    }
    // TC Kimlik validation
    if (formData.tcnumber.length !== 11 || !/^\d+$/.test(formData.tcnumber)) {
      setError('Geçerli bir TC Kimlik Numarası giriniz.');
      return false;
    }
    // Check if TC number ends with an even digit
    const lastDigit = parseInt(formData.tcnumber.slice(-1));
    if (lastDigit % 2 !== 0) {
      setError('TC Kimlik Numarası çift rakam ile bitmelidir.');
      return false;
    }

    // Phone validation
    if (formData.phoneNumber.length !== 11 || !formData.phoneNumber.startsWith('05')) {
      setError('Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (validateForm()) {
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  const handleBack = () => {
    setCurrentStep(STEPS.FORM);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    setCurrentStep(STEPS.COMPLETE);

    try {
      // Users.Ilce/Mahlist'e seçimdeki id'ler değil ADLAR virgülle yazılır.
      const { ilce, mahlist } = isDenetleyici
        ? ilceMahalle.kayitDegerleri()
        : { ilce: '', mahlist: '' };
      const payload = { ...formData, ilce, mahlist, isActive: true };
      const response = await createUser(payload);
      setCreatedUser(response.data);
      onSuccess(response.data);
    } catch (error) {
      // Backend kapsam/doğrulama hatalarını açıklayıcı mesajla döndürüyor (ör. e-posta öneki).
      setError(error?.response?.data?.message || 'Kullanıcı oluşturulurken bir hata oluştu.');
      console.error('Create user error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="stepper">
      <div className={`step ${currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.FORM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Kişi Bilgileri</span>
      </div>
      <div className={`step ${currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.CONFIRM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Bilgi Onayı</span>
      </div>
      <div className={`step ${currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.COMPLETE && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Tamamlandı</span>
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="create-user-form">
      <div className="form-group">
        <label htmlFor="name">Ad</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Ad"
        />
      </div>
      <div className="form-group">
        <label htmlFor="surname">Soyad</label>
        <input
          type="text"
          id="surname"
          name="surname"
          value={formData.surname}
          onChange={handleInputChange}
          placeholder="Soyad"
        />
      </div>
      <div className="form-group">
        <label htmlFor="tcnumber">TC Kimlik Numarası</label>
        <input
          type="text"
          id="tcnumber"
          name="tcnumber"
          value={formData.tcnumber}
          onChange={handleInputChange}
          placeholder="TC Kimlik Numarası"
          maxLength="11"
        />
      </div>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        {/* type="email" değil: tarayıcı Türkçe karakterli alan adlarını (güzelbahçe.com)
            geçersiz sayıp kaydı engelliyor ya da punycode'a çevirip e-postayı değiştiriyordu. */}
        <input
          type="text"
          inputMode="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Email"
        />
      </div>
      <div className="form-group">
        <label htmlFor="phoneNumber">Telefon Numarası</label>
        <input
          type="tel"
          id="phoneNumber"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          placeholder="05XXXXXXXXX"
          maxLength="11"
        />
        <span className="helper-text">Örnek: 05XXXXXXXXX (11 haneli)</span>
      </div>
      <div class="form-group" style={{display: 'none'}}>    <label htmlFor="grup">Grup</label>
      <select id="grup"
        name="grup"
        value={formData.grup || ""}
        onChange={(e) => handleGroupChange(formData.id, e.target.value)}
      
      >
        <option value="">Seçiniz</option>
        {groups.map((group) => (
          <option key={group.name} value={group.name}>
            {group.name}
          </option>
        ))}
      </select>
      </div>

      <div className="form-group">
        <label htmlFor="role">Rol</label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleInputChange}
          disabled={isRolesLoading}
        >
          <option value="">{isRolesLoading ? 'Yükleniyor...' : 'Seçiniz'}</option>
          {roles.map((role) => (
            <option key={role.id} value={String(role.id)}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      {isDenetleyici && <IlceMahalleSecimi secim={ilceMahalle} />}

      {error && <div className="error-message">{error}</div>}
      <div className="form-actions">
        <button type="submit" className="submit-button">
          Kişiyi Ekle
        </button>
      </div>
    </form>
  );

  const renderConfirmation = () => {
    const { ilce, mahlist } = isDenetleyici ? ilceMahalle.kayitDegerleri() : { ilce: '', mahlist: '' };
    const ilceName = ilce.split(',').join(', ');
    const mahalleNames = mahlist.split(',').join(', ');
    return (
    <div className="confirmation-step">
      <WarningIcon />
      <h3>Sisteme yeni bir kişi kaydetmek üzeresiniz. Onaylıyor musunuz?</h3>
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Ad Soyad</th>
              <th>TC Kimlik No</th>
              <th>Email</th>
              <th>Telefon</th>
              <th>Grup</th>
              <th>Rol</th>
              {isDenetleyici && (
                <>
                  <th>İlçe</th>
                  <th>Mahalle</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{`${formData.name} ${formData.surname}`}</td>
              <td>{formData.tcnumber}</td>
              <td>{formData.email}</td>
              <td>{formData.phoneNumber}</td>
              <td>{formData.grup}</td>
              <td><RoleBadge role={roles.find((r) => String(r.id) === formData.role)?.name ?? formData.role} /></td>
              {isDenetleyici && (
                <>
                  <td>{ilceName}</td>
                  <td>{mahalleNames}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" onClick={handleBack} className="back-button">
          İptal Et
        </button>
        <button type="button" onClick={handleConfirm} className="confirm-button">
          Sonraki Adım
        </button>
      </div>
    </div>
    );
  };

  const renderComplete = () => (
    <div className="complete-step">
      {isLoading ? (
        <div className="loading-state">
          <Spinner size="large" />
          <h3>Süreciniz tamamlanıyor...</h3>
          <button type="button" onClick={handleClose} className="cancel-button">
            Süreci İptal Et
          </button>
        </div>
      ) : createdUser ? (
        <div className="success-state">
          <SuccessCircle />
          <h3>Süreciniz tamamlandı. Aşağıdaki kişi sisteme eklendi.</h3>
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Ad Soyad</th>
                  <th>TC Kimlik No</th>
                  <th>Email</th>
                  <th>Telefon</th>
                  <th>Grup</th>
                  <th>Rol</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{`${createdUser.name} ${createdUser.surname}`}</td>
                  <td>{createdUser.tcnumber}</td>
                  <td>{createdUser.email}</td>
                  <td>{createdUser.phoneNumber}</td>
                  <td>{createdUser.grup}</td>
                  <td><RoleBadge role={roles.find((r) => String(r.id) === String(createdUser.role))?.name ?? createdUser.role} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" onClick={handleClose} className="close-button">
            Kapat
          </button>
        </div>
      ) : (
        <div className="error-state">
          <div className="error-icon">✕</div>
          <h3>Hata Oluştu</h3>
          <p>{error}</p>
          <button type="button" onClick={handleBack} className="retry-button">
            Tekrar Dene
          </button>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Yeni Kişi Ekle</h3>
        </div>
        {renderStepIndicator()}
        <div className="modal-body">
          {currentStep === STEPS.FORM && renderForm()}
          {currentStep === STEPS.CONFIRM && renderConfirmation()}
          {currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal; 